import type { HeatmapCell } from "@/api/types";
import { Panel } from "@/components/ui";
import { shortAddress } from "@/lib/formatters";
import { useChartColors } from "@/lib/theme";
import { useECharts } from "@/lib/use-echarts";

interface SpreadHeatmapPanelProps {
  data: HeatmapCell[];
  loading?: boolean;
}

export function SpreadHeatmapPanel({ data, loading }: SpreadHeatmapPanelProps) {
  const cc = useChartColors();
  // Filter out pools with corrupted spread values (overflow from missing token decimals)
  const clean = data.filter((d) => Math.abs(d.median_spread_bps) < 100_000);
  const pools = [...new Set(clean.map((d) => d.pool_address))];
  const poolLabels = pools.map((p) => shortAddress(p));

  const seriesData = clean.map((d) => [
    d.hour_of_day,
    pools.indexOf(d.pool_address),
    d.median_spread_bps,
  ]);

  const option = {
    backgroundColor: "transparent",
    grid: { top: 16, right: 80, bottom: 40, left: 90 },
    xAxis: {
      type: "category" as const,
      data: Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")),
      axisLabel: { fontSize: 10, color: cc.text },
      axisLine: { lineStyle: { color: cc.grid } },
    },
    yAxis: {
      type: "category" as const,
      data: poolLabels,
      axisLabel: { fontSize: 10, color: cc.text, fontFamily: "monospace" },
      axisLine: { lineStyle: { color: cc.grid } },
    },
    visualMap: {
      min: Math.min(-5, ...seriesData.map((d) => d[2] as number)),
      max: Math.max(5, ...seriesData.map((d) => d[2] as number)),
      calculable: true,
      orient: "vertical" as const,
      right: 0,
      top: "center",
      textStyle: { fontSize: 10, color: cc.text },
      formatter: (v: unknown) => `${(v as number).toFixed(1)} bps`,
      inRange: { color: ["#ef4444", "#1a1a2e", "#a855f7"] },
    },
    series: [
      {
        type: "heatmap" as const,
        data: seriesData,
        emphasis: { itemStyle: { borderColor: "#3b82f6", borderWidth: 1 } },
      },
    ],
    tooltip: {
      formatter: (params: unknown) => {
        const p = params as { value: number[] };
        const [hour, poolIdx, val] = p.value;
        return `${poolLabels[poolIdx as number] ?? poolIdx}<br/>Hour: ${hour}:00 UTC<br/>Spread: ${(val as number).toFixed(1)} bps`;
      },
    },
  };

  const containerRef = useECharts(option, [data, cc.grid]);

  return (
    <Panel
      title="SPREAD HEATMAP"
      description="Median CEX–DEX price gap by hour of day (UTC) and pool. Red = DEX price below Binance (arbitrage buy opportunity). Purple = DEX above Binance. Values in basis points (1 bps = 0.01%)."
    >
      <div style={{ position: "relative", height: 240 }}>
        <div ref={containerRef} style={{ height: 240 }} />
        {loading && (
          <div className="skeleton" style={{ position: "absolute", inset: 0, borderRadius: 4 }} />
        )}
      </div>
    </Panel>
  );
}
